import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { ManualClPositionGraphQlTransport } from "@/domains/thick-v1/positions"
import type { ManualClPositionHistoryRow } from "@/domains/validators"

/**
 * Observable store for the manage-position history tab. It prefers the
 * per-NFT NFPM lifecycle when a real token id is known, falling back to the
 * range-level Position feed for pre-NFPM rows.
 *
 * Unlike the repository-level position orchestrator this is an on-demand
 * surface: the composite calls `setActivePositionId(...)` on mount and again
 * with `undefined` on unmount so the store only fetches while the history
 * tab is actually rendered.
 */
export class PositionHistoryStore {
  rows: Array<ManualClPositionHistoryRow> = []
  activePositionId: string | undefined = undefined
  activeNfpmPositionId: string | undefined = undefined
  token0Symbol: string | undefined = undefined
  token1Symbol: string | undefined = undefined
  isLoading: boolean = false
  error: string | undefined = undefined

  constructor(private readonly transport: ManualClPositionGraphQlTransport) {
    makeObservable(this, {
      rows: observable,
      activePositionId: observable,
      activeNfpmPositionId: observable,
      token0Symbol: observable,
      token1Symbol: observable,
      isLoading: observable,
      error: observable,
      setActivePositionId: action,
      reload: action,
      hasRows: computed,
    })
  }

  get hasRows(): boolean {
    return this.rows.length > 0
  }

  setActivePositionId(positionId: string | undefined, nfpmPositionId?: string): void {
    if (positionId === this.activePositionId && nfpmPositionId === this.activeNfpmPositionId) return
    this.activePositionId = positionId
    this.activeNfpmPositionId = nfpmPositionId
    this.rows = []
    this.error = undefined
    this.token0Symbol = undefined
    this.token1Symbol = undefined
    if (positionId || nfpmPositionId) {
      void this.fetch()
    }
  }

  reload(): void {
    void this.fetch()
  }

  private async fetch(): Promise<void> {
    const positionId = this.activePositionId
    const nfpmPositionId = this.activeNfpmPositionId
    if (!positionId && !nfpmPositionId) return
    runInAction(() => {
      this.isLoading = true
      this.error = undefined
    })
    try {
      const response = await this.transport.loadPositionHistory(positionId, nfpmPositionId)
      // Bail out if the user navigated to a different position while the
      // fetch was in flight — stale response must not stomp the new one.
      if (this.activePositionId !== positionId || this.activeNfpmPositionId !== nfpmPositionId) return
      runInAction(() => {
        this.rows = response.history
        this.token0Symbol = response.token0Symbol
        this.token1Symbol = response.token1Symbol
        this.isLoading = false
      })
    } catch (error) {
      runInAction(() => {
        this.isLoading = false
        this.error = error instanceof Error ? error.message : "Failed to load position history"
      })
    }
  }
}
